'use client';

import { useMemo } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { alpha, Box, Typography } from '@mui/material';

import { useT } from '@/hooks';
import { EventResponse, OutcomeItem } from '@/types';
import { normalizeMarketQuestion } from '@/utils';

interface ResolvedTradeViewProps {
  event?: EventResponse;
  market?: OutcomeItem;
  winnerName?: string;
}

export function ResolvedTradeView({
  market,
  winnerName: manualWinnerName,
}: ResolvedTradeViewProps) {
  const { t } = useT();
  const winner = useMemo(() => {
    if (market) {
      let outcomesArray: any[] = [];
      if (typeof market.outcomes === 'string') {
        try {
          outcomesArray = JSON.parse(market.outcomes);
        } catch {
          // ignore
        }
      } else if (Array.isArray(market.outcomes)) {
        outcomesArray = market.outcomes;
      }
      return outcomesArray.find((o: any) => o.price === 1 || String(o.price) === '1');
    }
    return null;
  }, [market]);

  const winnerName = winner?.name || (winner as any)?.displayName || 'Unknown';
  const isNo = winnerName.toLowerCase() === 'no';
  const color = isNo ? 'error.main' : 'success.main';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2.5,
        py: 6,
        px: 4,
        textAlign: 'center',
        minHeight: 300,
      }}
    >
      {/* Icon Circle */}
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          bgcolor: (theme) =>
            alpha(isNo ? theme.palette.error.main : theme.palette.success.main, 0.1),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: (theme) =>
            `0 8px 16px ${alpha(isNo ? theme.palette.error.main : theme.palette.success.main, 0.2)}`,
        }}
      >
        <CheckIcon sx={{ color: color, fontSize: 44, fontWeight: 'bold' }} />
      </Box>

      {/* Outcome Text */}
      <Typography
        variant="h5"
        sx={{
          color: color,
          fontWeight: 700,
          fontSize: '1.75rem',
        }}
      >
        {t('sports.outcome', { defaultValue: 'Outcome' })}: {winnerName}
      </Typography>

      {/* Market Question */}
      {market && (
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            fontWeight: 600,
            fontSize: '1rem',
            opacity: 0.8,
            maxWidth: '280px',
          }}
        >
          {normalizeMarketQuestion(market as any)}
        </Typography>
      )}
    </Box>
  );
}
